/* eslint-disable radix */
import { format } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { Component, OnInit, Input, ChangeDetectorRef, OnDestroy, HostListener } from '@angular/core';
import { DatePipe } from '@angular/common';
import { Router } from '@angular/router';
import cloneDeep from 'lodash.clonedeep';
import { AppConfigService, AppBrandingService, DropdownOption } from '@eucoms/common-components';

import { FallbackReportService } from '@app/fallback-report/local-service/fallback-report.service';
import { DataSourceService } from '@app/fallback-report/data-source.service';
import { TabularData, factoryTabularData } from './tabular-data.interface';
import { JSONTabularData } from './tabular-data.interface';
import { DATA_UPDATED, NUMBER_OF_OUTAGES, CUSTOMERS_AFFECTED, CUSTOMERS_SERVED, CUSTOMERS_RESTORED } from './constants';
import copy from './copy';

declare type Timer = any;

/**
 * this component renders the table and condintionally also renders the table
 * nav (in mobile view). It initiates the call for data, formats it, and
 * applies filter and sort order criteria.
 *
 * here be dragons
 **/
@Component({
  selector: 'app-fallback-table',
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.scss', './table-summary.scss']
})
export class TableComponent implements OnInit, OnDestroy {
  opCo = this.appConfig.config.opCo;
  isBGEStorm = false;

  pollingInterval = (this.localService.isComEd) ? 15 * 60 * 1000 : 10 * 60 * 1000;

  copyTotalOutages = copy.TotalOutages;
  copyTotalAffected = copy.TotalAffected;
  copyTotalServed = copy.TotalServed;
  copyTotalRestored = copy.TotalRestored;
  copyLastUpdated = copy.LastUpdated;
  copyUpdateNotice = (this.localService.isComEd) ? copy.ComEdUpdateNotice : copy.UpdateNotice;

  // variables for PECO and ACE county filter
  selectedCountyFilter: DropdownOption;
  filterCounties: Array<DropdownOption> = [];
  isShowCountyFilter = false;
  tableDataRows: Array<any> = [];

  // specific for ACE
  aceMoreInfoDataRows: Array<any> = [];
  currentOutageInfo: Array<any> = [];
  projectedRestCust: Array<any> = [];
  electricInfra: Array<any> = [];

  // MARK - various matrices regarding the data to display

  /** last time data was updated fo rthis endpoint */
  lastUpdate: { [endpoint: string]: number } = {};

  /** the 'raw' data to display currently */
  get tabularData() {
    return this.tabularDataAtEndpoint[this.endpoint] || factoryTabularData();
  }

  /** a collection of raw data, facilitating the navigation between tabs without reloading data when tabs change. */
  tabularDataAtEndpoint: { [endpoint: string]: TabularData } = {};

  // MARK - processing data to display

  _fields: Array<string>;
  get fields() {
    if (!this.isBGEStorm) {
      this._fields = this._fields.filter(field => field !== 'CUSTOMERS_RESTORED');
    }
    return this._fields;
  }
  set fields(value: Array<string>) {
    this._fields = value || [];
  }

  sortColumns: { [key: string]: boolean | undefined } = {};
  get sortAscending() {
    return Object.values(this.sortColumns)
      .find(value => value !== undefined);
  }
  get sortColumn() {
    const sortAscending = Object.values(this.sortColumns)
      .find(value => value !== undefined);

    if (sortAscending === undefined) {
      return undefined;
    }

    return Object.keys(this.sortColumns)
      .find(key => this.sortColumns[key] === sortAscending);
  }

  get sortedFilteredData() {
    let data = cloneDeep(this.tabularData.data); // dont' mess with real data

    // MARK - filter
    if (this.search) {
      data = data.filter(row => this.filter.test(row[0].toLowerCase()));
    }

    // MARK - sort
    const sortAscending = Object.values(this.sortColumns)
      .find(value => value !== undefined);
    if (sortAscending !== undefined) {
      const sortColumn = Object.keys(this.sortColumns)
        .find(key => this.sortColumns[key] === sortAscending);

      data = this.isNumericDataColumn(parseInt(sortColumn))
        ?
        data.sort((a, b) => {
          const left = parseFloat(a[sortColumn]);
          const right = parseFloat(b[sortColumn]);

          if (left === right) {
            return 0;
          }
          if (left === null) {
            return 1;
          }
          if (right === null) {
            return -1;
          }
          if (sortAscending) {
            return left > right ? 1 : -1;
          } else {
            return left < right ? 1 : -1;
          }
        })
        :
        data.sort((a, b) => {
          a = a[sortColumn].toLowerCase();
          b = b[sortColumn].toLowerCase();

          if (a === b) {
            return 0;
          }
          if (a === null) {
            return 1;
          }
          if (b === null) {
            return -1;
          }
          if (sortAscending) {
            return a > b ? 1 : -1;
          } else {
            return a < b ? 1 : -1;
          }
        });
    }

    return data;
  }

  // MARK  other class properties
  totalOutages: number;
  totalAffected: number;
  totalServed: number;
  totalRestored: number;

  get opCoColor() {
    return this.appBranding.theme['--primaryBrand'];
  }

  search: string;
  get filter() {
    return new RegExp(this.search.replace(/[^A-Za-z0-9 ]/g, '').replace(/\s+/, '\\s+').toLowerCase());
  }

  get celStyle() {
    for (const member in this.localService.DataEndpoint) {
      if (this.localService.DataEndpoint[member] === this.endpoint) {
        return member;
      }
    }
  }

  private interval: Timer;
  private _endpoint: string;

  @Input() set endpoint(endpoint: string) {
    this._endpoint = endpoint;

    if (this.tabularData.data.length) {
      let index: number = this.localService.DataFields[this.localService.endpointKey].indexOf('NUMBER_OF_OUTAGES');
      this.totalOutages = this.tabularData.data.reduce((p, c) => p + parseInt('' + c[index]), 0);

      index = this.localService.DataFields[this.localService.endpointKey].indexOf('CUSTOMERS_AFFECTED');
      this.totalAffected = this.tabularData.data.reduce((p, c) => p + parseInt('' + c[index]), 0);

      index = this.localService.DataFields[this.localService.endpointKey].indexOf('CUSTOMERS_SERVED');
      this.totalServed = this.tabularData.data.reduce((p, c) => p + parseInt('' + c[index]), 0);

      this.isBGEStorm = (this.localService.isBGE && this.tableDataRows[this._endpoint].find(row => parseInt(row.STORM_FLAG) === 1));
      if (this.isBGEStorm) {
        index = this.localService.DataFields[this.localService.endpointKey].indexOf('CUSTOMERS_RESTORED');
        this.totalRestored = this.tabularData.data.reduce((p, c) => p + parseInt('' + c[index]), 0);
      }

      if (this.localService.isPECO) {
        this.selectedCountyFilter = this.filterCounties[0];
        this.setDropdownValue();
      }
    }

    if (new Date().getTime() - (this.lastUpdate[endpoint] || 0) >= this.pollingInterval) {
      this.getData();
    }
  }

  get endpoint() {
    return this._endpoint;
  }

  isGettingData: { [endpoint: string]: number } = {};

  @HostListener('visibilitychange', ['$event'])
  handleTabRefocusing(event: Event) {
    this.cd.detectChanges();
  }

  constructor(
    private dataSource: DataSourceService,
    private localService: FallbackReportService,
    private appConfig: AppConfigService,
    private appBranding: AppBrandingService,
    private cd: ChangeDetectorRef,
    private datePipe: DatePipe,
    private router: Router
  ) { }

  ngOnInit() {
    this.getData();

    if (this.localService.isACE) {
      this.dataSource.getACEMoreInfo().subscribe(res => {
        this.aceMoreInfoDataRows = res;
      });
    }

    this.interval = setInterval(this.getData.bind(this), this.pollingInterval);
  }

  ngOnDestroy() {
    clearInterval(this.interval);
  }

  /**
   * a handler to set tabularDataAtEndpoint and ensure the side effect of lastUpdate
   * @param endpoint - the endpoint (property of this class)
   * @param data - the data from that endpoint
   */
  private setTabularDataAtEndpoint(endpoint: string, data: TabularData) {
    this.tabularDataAtEndpoint[endpoint] = data;
    this.lastUpdate[endpoint] = new Date().getTime();
  }

  /**
   * how to retrieve data from the class property endpoint
   */
  private getData() {
    if (this.localService.isPECO || this.localService.isACE) {
      this.selectedCountyFilter = this.filterCounties[0];
    }

    if (!this.isGettingData.hasOwnProperty(this.endpoint)) {
      this.isGettingData[this.endpoint] = 0;
    }
    if (this.isGettingData[this.endpoint]) {
      return;
    }
    this.isGettingData[this.endpoint]++;

    this.dataSource.get(this.endpoint).subscribe((res: Array<JSONTabularData>) => {
      this.tableDataRows[this.endpoint] = res.filter(r => r.PLACE_NAME !== null);

      if (this.tableDataRows[this.endpoint] !== undefined && this.tableDataRows[this.endpoint].length > 0) {
        this.tableDataRows[this.endpoint].map((row: any) => {
          if (row.CUSTOMERS_AFFECTED === null || row.CUSTOMERS_AFFECTED === undefined) {
            row.CUSTOMERS_AFFECTED = 0;
          }
          if (row.CUSTOMERS_SERVED === null || row.CUSTOMERS_SERVED === undefined) {
            row.CUSTOMERS_SERVED = 0;
          }
          if (row.PERCENT_AFFECTED === null || row.PERCENT_AFFECTED === undefined) {
            row.PERCENT_AFFECTED = 0;
          }
          if (row.NUMBER_OF_OUTAGES === null || row.NUMBER_OF_OUTAGES === undefined) {
            row.NUMBER_OF_OUTAGES = 0;
          }

          if (this.localService.isBGE) {
            if (row.CUSTOMERS_RESTORED === null || row.CUSTOMERS_RESTORED === undefined) {
              row.CUSTOMERS_RESTORED = 0;
            }
            if (row.PERCENT_AFFECTED === 'Less Than 5%') {
              row.PERCENT_AFFECTED = 1;
            } else {
              if (row.PERCENT_AFFECTED.toString().slice(row.PERCENT_AFFECTED.length - 1) === '%') {
                row.PERCENT_AFFECTED = row.PERCENT_AFFECTED.toString().slice(0, -1);
              }
              row.PERCENT_AFFECTED = parseFloat(row.PERCENT_AFFECTED);
            }
          }
        });

        this.isBGEStorm = (this.localService.isBGE && this.tableDataRows[this.endpoint].find(row => parseInt(row.STORM_FLAG) === 1));

        if ((this.localService.isPECO || this.localService.isACE) && this.filterCounties.length === 0) {
          this.isShowCountyFilter = true;
          this.getCountyFilters();
        }

        this.processData(this.tableDataRows[this.endpoint], this.endpoint);
      }
    });
  }

  /**  prepare received data for rendering */
  private processData(data: Array<JSONTabularData>, endpoint: string) {
    this.isGettingData[endpoint]--;

    const sample = data[0] || {};
    const lastUpdated = sample[DATA_UPDATED] as string;

    const field = Object.keys(this.localService.DataEndpoint)
      .find(k => this.localService.DataEndpoint[k] === endpoint);
    this.fields = this.localService.DataFields[field];

    let headers = this.localService.TableRowHeaders[field];
    if (!this.isBGEStorm && headers !== undefined) {
      headers = headers.filter(header => header !== 'CUSTOMERS RESTORED');
    }

    this.setTabularDataAtEndpoint(endpoint, {
      date: (lastUpdated && !this.localService.isComEd) ?
        formatInTimeZone(lastUpdated, 'America/New_York', 'MMM dd, h:mm a') :
        (lastUpdated && this.localService.isComEd) ?
          formatInTimeZone(lastUpdated, 'America/Chicago', 'MMM dd, h:mm a') :
          format(new Date(), 'MMM dd, h:mm a'),
      fields: this.fields,
      headers,
      data: data.map(row => this.fields.map(f => row[f]))
    });

    this.totalOutages = data.reduce((p, c) => p + parseInt('' + c[NUMBER_OF_OUTAGES]), 0);
    this.totalAffected = data.reduce((p, c) => p + parseInt('' + c[CUSTOMERS_AFFECTED]), 0);
    this.totalServed = data.reduce((p, c) => p + parseInt('' + c[CUSTOMERS_SERVED]), 0);

    if (this.isBGEStorm) {
      this.totalRestored = data.reduce((p, c) => p + parseInt('' + c[CUSTOMERS_RESTORED]), 0);
    }

    this.sortColumns[0] = true;
  }

  /** on user toggle column sort */
  onToggleColumn(i: number) {
    if (this.tabularData.headers[i] !== 'MORE') {
      const value = this.sortColumns[i] === undefined
        ? true
        : !this.sortColumns[i];

      this.sortColumns = {};
      this.sortColumns[i] = value;

      this.cd.detectChanges();
    }
  }

  /** helper method to indicate how to render a cel */
  isPlainText(i: number) {
    switch (this.opCo) {
      case 'ComEd':
      case 'BGE':
      case 'PECO':
      case 'DPL':
      case 'PEPCO':
        return true;
      case 'ACE':
        return this.localService.TableStyle[i] !== this.localService.TableStyle.ACELink;
      default:
        console.warn('[TableComponent::isPlainText] no cel style definition for opCo', this.opCo);
    }
  }

  /**
   * helper method reflecting if a column's data is numeric. Note that not
   * every column's data is rendered, and so this may not strictly agree with
   * the isPlainText and other helper methods for rendering columnar data
   **/
  private isNumericDataColumn(i: number) {
    const celStyles = this.localService.TableCelStyles[this.localService.endpointKey];
    if (!celStyles) {
      return null; // this should never happen but in spec it does unless we commit to a default opco extension to test with.
    }

    return celStyles[this.fields[i]] !== this.localService.TableStyle.Default;
  }

  /** function to set value of county filter for ACE and PECO */
  setDropdownValue() {
    if (this.selectedCountyFilter.value !== 'All Locations') {
      const filteredData = this.tableDataRows[this.endpoint].filter((row: any) => row.COUNTY === this.selectedCountyFilter.value);
      this.processData(filteredData, this.endpoint);
    } else {
      this.processData(this.tableDataRows[this.endpoint], this.endpoint);
    }
  }

  /** function to get county filter values for ACE and PECO */
  getCountyFilters() {
    let index = 1;

    this.tableDataRows[this.endpoint].map(row => {
      if (!this.filterCounties.find(county => county.value === row.COUNTY) && row.COUNTY !== undefined && row.COUNTY !== null) {
        this.filterCounties.push({ id: index.toString(), value: row.COUNTY });
        index += 1;
      }
    });

    this.filterCounties.sort(function (a: any, b: any) {
      return a.value < b.value ? -1 : a.value > b.value ? 1 : 0;
    });

    if (!this.filterCounties.find(county => county.value === 'All Locations')) {
      this.filterCounties.unshift({ id: '0', value: 'All Locations' });
    }

    this.selectedCountyFilter = this.filterCounties[0];
  }

  /** function to check if more info icon will display */
  isShowMoreInfo(rowObj: any, i: number) {
    return rowObj[i - 1] > 0 && this.localService.isACE;
  }

  /** function to get ACE more info */
  getACEMoreInfoData(row: any) {
    this.currentOutageInfo = [];
    this.projectedRestCust = [];
    this.electricInfra = [];

    const dataToPass = [];

    if (this.aceMoreInfoDataRows !== undefined && this.aceMoreInfoDataRows.length > 0) {
      const aceMoreInfo = this.aceMoreInfoDataRows.filter(data => data.PLACE_NAME === row[0]);

      aceMoreInfo[0].CUSTOMERS_AFFECTED =
        (aceMoreInfo[0].CUSTOMERS_AFFECTED < 5 && aceMoreInfo[0].CUSTOMERS_AFFECTED > 0)
          ? 'Fewer Than 5' : aceMoreInfo[0].CUSTOMERS_AFFECTED;

      aceMoreInfo[0].PERCENT_AFFECTED =
        (aceMoreInfo[0].PERCENT_AFFECTED < 5 && aceMoreInfo[0].PERCENT_AFFECTED > 0)
          ? 'Less Than 5%' : (aceMoreInfo[0].PERCENT_AFFECTED.toString() + '%');

      aceMoreInfo[0].EARLIEST_REPORT = this.datePipe.transform(aceMoreInfo[0].EARLIEST_REPORT, 'MMMM d, y h:mm aaaaa\'m\'');
      aceMoreInfo[0].ESTIMATED_RESTORATION = this.datePipe.transform(aceMoreInfo[0].ESTIMATED_RESTORATION, 'MMMM d, y h:mm aaaaa\'m\'');

      this.currentOutageInfo.push(
        { label: 'NAME OF MUNICIPALITY', value: aceMoreInfo[0].PLACE_NAME },
        { label: 'CUSTOMERS AFFECTED', value: aceMoreInfo[0].CUSTOMERS_AFFECTED },
        { label: 'CUSTOMERS SERVED', value: aceMoreInfo[0].CUSTOMERS_SERVED },
        { label: 'PERCENT AFFECTED', value: aceMoreInfo[0].PERCENT_AFFECTED },
        { label: 'NUMBER OF OUTAGE ORDERS', value: aceMoreInfo[0].NUMBER_OF_OUTAGES },
        { label: 'EARLIEST REPORT', value: aceMoreInfo[0].EARLIEST_REPORT },
        { label: 'ESTIMATED RESTORATION', value: aceMoreInfo[0].ESTIMATED_RESTORATION }
      );

      this.getProjectedCustRestData(aceMoreInfo[0]);

      // this will be used once data for electric infra is available
      // this.electricInfra.push(
      //   { label: 'NUMBER OF UTILITY POLES', value: 0 },
      //   { label: 'MILES OF DISTRIBUTION LINES', value: 0 },
      //   { label: 'NUMBER OF SUBSTATIONS SERVING', value: 0 },
      //   { label: 'NUMBER OF CIRCUITS SERVING', value: 0 }
      // );

      dataToPass.push({
        currentOutageInfo: this.currentOutageInfo,
        projectedRestCust: this.projectedRestCust,
        electricInfra: this.electricInfra,
        totalOutages: aceMoreInfo[0].NUMBER_OF_OUTAGES
      });

      this.localService.aceMoreData = dataToPass;
    }

    this.localService.isShowMore = true;
  }

  getProjectedCustRestData(data: any) {
    if (data.RESTPROJECTEDTODAY > 0) {
      const dateToday = new Date();
      this.projectedRestCust.push({ label: dateToday, key: 'RESTPROJECTEDTODAY', value: data.RESTPROJECTEDTODAY });
    }
    if (data.RESTPROJECTEDTOMORROW > 0) {
      const dateNext = new Date();
      dateNext.setDate(dateNext.getDate() + 1);

      this.projectedRestCust.push({ label: dateNext, key: 'RESTPROJECTEDTOMORROW', value: data.RESTPROJECTEDTOMORROW });
    }
    if (data.RESTPROJECTEDPLUSTWO > 0) {
      const dateNext2 = new Date();
      dateNext2.setDate(dateNext2.getDate() + 2);

      this.projectedRestCust.push({ label: dateNext2, key: 'RESTPROJECTEDPLUSTWO', value: data.RESTPROJECTEDPLUSTWO });
    }
    if (data.RESTPROJECTEDPLUSTHREE > 0) {
      const dateNext3 = new Date();
      dateNext3.setDate(dateNext3.getDate() + 3);

      this.projectedRestCust.push({ label: dateNext3, key: 'RESTPROJECTEDPLUSTHREE', value: data.RESTPROJECTEDPLUSTHREE });
    }
    if (data.RESTPROJECTEDEXTENDED > 0) {
      const dateNext4 = new Date();
      dateNext4.setDate(dateNext4.getDate() + 4);

      this.projectedRestCust.push({ label: dateNext4, key: 'RESTPROJECTEDEXTENDED', value: data.RESTPROJECTEDEXTENDED });
    }
  }
}
